<template>
  <PageDynamic :path="pagePath" :title="pageTitle" :description="pageDescription">

    <h1 class="sr-only">About Us</h1>

    <div v-if="pageContent" v-html="pageContent"></div>

  </PageDynamic>

</template>

<script setup>

import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import { usePageContentStore } from "@/stores/pageContent";
import { useShortcodeParser } from "~/composables/useShortcodeParser";
import { useBreadcrumbSchema } from "~/composables/useBreadcrumbSchema";
import Spinner from "@/components/helpers/Spinner.vue";
import PageDynamic from "@/components/pages/Dynamic.vue";


const store = usePageContentStore();
const route = useRoute();
const { parseShortcodes } = useShortcodeParser();
const { public: { siteUrl } } = useRuntimeConfig();

// SSR-safe metadata fetch (same pattern as pages/home.vue) — populates
// title/description before render, so prerendered/SSR HTML has real values
// instead of empty ones. The client-only fetchPageContent below (which also
// parses shortcodes) reuses this same response instead of fetching again.
await store.fetchPageMeta(route.path);

const pageTitle = computed(() => store.title);
const pageDescription = computed(() => store.description);
const isLoading = computed(() => store.loading);
const pagePath = computed(() => store.path || route.path.replace(/^\/(en|th)/, ""));
const pageContent = ref("");

const pageUrl = computed(() => `${siteUrl}${route.path}`);

useHead(() => {
  // Last crumb is always this page — index from the end so a future CMS
  // re-nesting under a parent can't silently swap in the ancestor's name.
  const crumbs = store.breadcrumbs;
  const pageName = crumbs[crumbs.length - 1]?.title?.trim() || "About Us";
  const breadcrumbItems = [
    { name: "Home", url: `${siteUrl}/` },
    { name: pageName, url: pageUrl.value },
  ];

  return {
    script: [
      {
        type: "application/ld+json",
        innerHTML: JSON.stringify(useBreadcrumbSchema(breadcrumbItems, pageUrl.value)),
      },
      {
        type: "application/ld+json",
        innerHTML: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "@id": `${pageUrl.value}#webpage`,
          url: pageUrl.value,
          name: pageName,
          about: { "@id": `${siteUrl}/#organization` },
          mainEntity: { "@id": `${siteUrl}/#organization` },
        }),
      },
    ],
  };
});


const loadScript = (src, isAsync = true, isDefer = true) => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = isAsync;
    script.defer = isDefer;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
};

const injectCustomHead = async (customHead) => {
  const head = document.head;
  const headFragment = document.createRange().createContextualFragment(customHead);
  head.appendChild(headFragment);
};

const injectCustomFooter = async (customFooter) => {
  const body = document.body;
  const footerFragment = document.createRange().createContextualFragment(customFooter);

  // ดึง script จาก customFooter
  const scripts = footerFragment.querySelectorAll('script[src]');
  const scriptUrls = Array.from(scripts).map((script) => script.src);

  // Append footer HTML ยกเว้น script
  scripts.forEach((script) => script.remove());
  body.appendChild(footerFragment);

  // โหลด script ทีละตัว
  for (const src of scriptUrls) {
    try {
      await loadScript(src);
    } catch (error) {
      console.error(`Failed to load script: ${src}`, error);
    }
  }
};

// Fetch the page content based on the route's slug
const fetchPageContent = async () => {
  try {
    // const slug = Array.isArray(route.params.slug)
    //   ? route.params.slug.join("/")
    //   : route.params.slug;

    const path = route.path;
    await store.fetchPageContent(path);
    pageContent.value = store.content;

    if (store.content.custom_head) {
      injectCustomHead(store.content.custom_head);
    }
    if (store.content.custom_footer) {
      injectCustomFooter(store.content.custom_footer);
    }

  } catch (error) {
    console.error("Error fetching page content:", error);
  }
};
const onScroll = () => {
  const scrollElement = document.getElementById("scroll-element");
  const businessContent = document.querySelector(".business-content");
  const milestoneContent = document.querySelector(".milestones-content");
  const businessContentBottom =
    businessContent?.getBoundingClientRect().bottom || 0;
  const milestoneContentBottom =
    milestoneContent?.getBoundingClientRect().bottom || 250;
  const windowHeight = window.innerHeight;


    if (window.scrollY > 100) {
      scrollElement?.classList.remove("is-opacity");
      document.body.classList.add("is-black");
    } else {
      scrollElement?.classList.add("is-opacity");
      document.body.classList.remove("is-black");
    }

    if (businessContentBottom <= windowHeight) {
      document.body.classList.remove("is-black");
    }

    if (milestoneContentBottom <= windowHeight) {
      document.body.classList.add("is-black");
    }

    if (window.scrollY < 100) {
      document.body.classList.remove("is-black");
    }
 
};
onMounted(() => {
  if (document.body) {
    window.addEventListener("scroll", onScroll);
  }
  fetchPageContent();
  
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScroll);
});


</script>

<style lang="scss">
@import "@/assets/scss/app";
@import "@/assets/scss/pages/about-us";
</style>
